/**
 * usePasteUpload Hook
 *
 * 处理聊天界面的粘贴文件上传功能
 *
 * 功能特点：
 * - 监听粘贴事件，检测剪贴板中的文件
 * - 支持粘贴图片、文档等文件类型
 * - 只有检测到文件时才阻止默认粘贴行为
 * - 与现有Uppy上传流程无缝集成
 * - 支持全局监听和局部监听两种模式
 *
 * 技术实现：
 * - 检测 clipboardData.items 中的文件类型项
 * - 复用现有的文件验证和错误处理机制
 * - 使用现有的 enqueueFilesAtom 添加文件到上传队列
 *
 * 使用场景：
 * - 截图后直接 Ctrl+V 粘贴
 * - 复制文件后粘贴到聊天界面
 * - 从其他应用复制图片后粘贴
 */
import { useCallback, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { createStandaloneToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { layoutModeAtom } from '../../atoms/v3/uiAtoms';

import { enqueueFilesAtom, uppyInstanceAtom } from '../../atoms/v3/uppyAtoms';
import { validateFileTypeAndSize } from '../../utils/fileValidation';
import { trackEvent } from '../../lib/analytics';

// 创建独立的 toast 实例
const { toast } = createStandaloneToast();

interface UsePasteUploadReturn {
  /** 处理粘贴事件 */
  handlePaste: (e: React.ClipboardEvent | ClipboardEvent) => void;
}

interface UseGlobalPasteUploadOptions {
  /** 是否启用全局监听 */
  enableGlobalListener?: boolean;
  /** 是否只在聊天页面生效 */
  onlyInChatPages?: boolean;
}

export const usePasteUpload = (options: UseGlobalPasteUploadOptions = {}): UsePasteUploadReturn => {
  const { enableGlobalListener = false, onlyInChatPages = true } = options;
  const uppy = useAtomValue(uppyInstanceAtom);
  const enqueueFiles = useSetAtom(enqueueFilesAtom);
  const { t } = useTranslation(['v3/chat', 'common']);
  const layoutMode = useAtomValue(layoutModeAtom);

  const handlePaste = useCallback((e: React.ClipboardEvent | ClipboardEvent) => {
    console.log('🔥 usePasteUpload: handlePaste triggered');

    if (!uppy) {
      console.warn('粘贴上传失败: Uppy实例不存在');
      return;
    }

    const items = e.clipboardData?.items;
    if (!items) {
      console.warn('🔥 usePasteUpload: no clipboardData.items available');
      return;
    }
    let hasFiles = false;
    const validFiles: File[] = [];

    console.log('🔥 usePasteUpload: clipboardData.items count:', items.length);

    // 检查剪贴板中的所有项目
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      console.log(`🔥 usePasteUpload: item ${i} - kind: ${item.kind}, type: ${item.type}`);

      // 只处理文件类型的项目
      if (item.kind === 'file') {
        hasFiles = true;
        const file = item.getAsFile();
        console.log('🔥 usePasteUpload: found file:', file?.name, file?.type, file?.size);
        
        if (file) {
          try {
            // 复用现有的文件验证逻辑
            validateFileTypeAndSize({
              type: file.type,
              size: file.size,
              name: file.name || `pasted-${file.type.split('/')[1] || 'file'}-${Date.now()}`
            });

            // 验证通过，添加到有效文件列表
            validFiles.push(file);
          } catch (error) {
            // 复用现有的错误处理逻辑
            const errorMessage = error instanceof Error ? error.message : '';
            if (errorMessage.includes('不支持的文件类型')) {
              // 记录用户尝试上传不支持的文件类型
              const fileExtension = file.name?.split('.').pop()?.toLowerCase() || 'unknown';
              const fileType = file.type || 'unknown';

              // 发送埋点事件
              trackEvent('file upload unsupported type', {
                file_extension: fileExtension,
                file_type: fileType,
                file_size: file.size,
                error_message: errorMessage,
                upload_source: 'paste'
              });

              // 显示友好的错误提示
              toast({
                title: t('common:errors.invalidFileType'),
                description: t('common:errors.invalidFileTypeDescription'),
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top'
              });
            } else {
              // 其他错误（如文件大小超限）
              toast({
                title: t('common:errors.fileUploadError'),
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top'
              });
            }
          }
        }
      }
    }

    // 如果检测到文件，阻止默认粘贴行为（避免将文件内容粘贴到文本框）
    if (hasFiles) {
      e.preventDefault();
      
      // 只将有效文件添加到队列
      if (validFiles.length > 0) {
        // 记录成功验证的文件类型
        validFiles.forEach(file => {
          const fileExtension = file.name?.split('.').pop()?.toLowerCase() || 'unknown';
          trackEvent('file upload valid type', {
            file_extension: fileExtension,
            file_type: file.type || 'unknown',
            file_size: file.size,
            upload_source: 'paste'
          });
        });

        // 将File对象转换为MinimalRequiredUppyFileType格式
        const uppyFiles = validFiles.map(file => ({
          name: file.name || `pasted-${file.type.split('/')[1] || 'file'}-${Date.now()}`,
          type: file.type,
          data: file,
          meta: {
            name: file.name || `pasted-${file.type.split('/')[1] || 'file'}-${Date.now()}`,
            type: file.type,
            source: 'paste'
          }
        }));

        // 使用现有的文件入队机制
        enqueueFiles(uppyFiles);

        // 显示成功提示
        toast({
          title: t('v3/chat:pasteUploadSuccess', '文件粘贴成功'),
          description: t('v3/chat:pasteUploadSuccessDesc', '已添加 {{count}} 个文件到上传队列', { count: validFiles.length }),
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
      }
    }
    
    // 如果没有检测到文件，让默认粘贴行为继续（粘贴文本）
  }, [uppy, enqueueFiles, t]);

  // 全局粘贴监听
  useEffect(() => {
    if (!enableGlobalListener) return;

    const handleGlobalPaste = (e: ClipboardEvent) => {
      // 检查是否在聊天页面（如果启用了页面限制）
      if (onlyInChatPages) {
        const path = window.location.pathname;

        // 桌面端：聊天功能在右侧栏中，任何页面都可能有聊天功能
        // 移动端：只在特定的聊天页面才有聊天功能
        const isChatPage = layoutMode === 'desktop'
          ? true // 桌面端任何页面都有聊天功能（右侧栏）
          : (path.includes('/v3/chat') || path.includes('/v3/taskv2')); // 移动端只在特定页面

        if (!isChatPage) {
          console.log('🔥 usePasteUpload: not in chat page, skipping global paste');
          return;
        }
      }

      console.log('🔥 usePasteUpload: global paste triggered');

      // 检查是否在输入框中
      const activeElement = document.activeElement;
      const isInInput = activeElement?.tagName === 'INPUT' ||
                       activeElement?.tagName === 'TEXTAREA' ||
                       (activeElement as HTMLElement)?.contentEditable === 'true';

      if (isInInput) {
        console.log('🔥 usePasteUpload: in input element, letting local handler take precedence');
        // 在输入框中时，让 TextInput 组件的 onPaste 处理器优先处理
        // 这样可以确保事件处理的顺序和优先级
        return;
      }

      // 转换为 React.ClipboardEvent 兼容的格式
      const reactEvent = {
        clipboardData: e.clipboardData,
        preventDefault: () => e.preventDefault(),
        stopPropagation: () => e.stopPropagation()
      } as React.ClipboardEvent;

      handlePaste(reactEvent);
    };

    console.log('🔥 usePasteUpload: adding global paste listener');
    window.addEventListener('paste', handleGlobalPaste);

    return () => {
      console.log('🔥 usePasteUpload: removing global paste listener');
      window.removeEventListener('paste', handleGlobalPaste);
    };
  }, [enableGlobalListener, onlyInChatPages, handlePaste, layoutMode]);

  return {
    handlePaste
  };
};
